// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type { Point } from 'highcharts';
import { withStyles } from 'common/util/react';
import { FlatSegment, InsightsQueryResults } from 'common/report/insights/util/types';
import { ChartType, InsightsBookmarkParams } from 'common/types/reports/bookmark';
import { ChartDataItem } from 'common/charts/visualization/pie-chart/types';
import { SpinnerReact } from 'common/components/spinner';
import { EmptyDataReact } from 'common/charts/misc/empty-data';
import type { PopoverActionCallback, PopoverCallback } from 'common/charts/types';
import { ContextMenuReact } from 'common/widgets/context-menu';
import { OTHER_SEGMENT_VALUE } from 'common/report/insights/util/constants';
import { colors } from 'common/util/colors';
import { computeMetricClauseByKey, renderPieChartTooltip } from 'common/report/insights/util/tooltip';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { getInsightsAggregateCsvExportLimit, getWorkspaceId, isPublicMode } from 'common/report/util';
import { REPORT_LIMITS, ReportName } from 'common/report/constants';
import { canShowOtherSegment, getSingleNumberFromSegmentValue, getSortConfig } from 'common/report/insights/util';
import { ParamsAndResults } from 'common/report/types';
import { deselectPoint, selectPoint } from 'common/report/util/highcharts';
import { renamePropertyValue } from 'common/report/util/properties';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { formatMetricNumberWithUnits } from 'common/util';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { ClauseType } from 'iron/common/report/insights/models/types';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useTranslation } from 'common/i18n/hooks';
import { SegmentLegendHeader } from 'common/charts/misc/segment-legend-header';
import { PieChartReact } from 'common/charts/visualization/pie-chart';
import { getFinalDisplayOptions } from '../util';
import css from './index.inline.styl';
import { getOtherSegment, getPieChartData, PieChartData } from './util';
export type InsightsPieChartProps = {
  dataGroups?: Record<string, DataGroup>;
  failed?: boolean;
  loaded: boolean;
  onPopoverAction?: PopoverActionCallback;
  paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;
  popoverCallback?: PopoverCallback;
};
export const InsightsPieChart = (props: InsightsPieChartProps) => {
  var _useCurrentProject;
  const [bookmarkParams, setBookmarkParams] = useState<InsightsBookmarkParams | null>(null);
  const [pieChartData, setPieChartData] = useState<PieChartData | null>(null);
  const [popoverSegment, setPopoverSegment] = useState<FlatSegment | null>(null);
  const [popoverSelection, setPopoverSelection] = useState<Point | null>(null);
  const [popoverStyles, setPopoverStyles] = useState<{
    visibility?: string;
    left?: string;
    top?: string;
  } | null>(null);
  const [results, setResults] = useState<InsightsQueryResults | null>(null);
  const i18n = useTranslation();
  const metrics = useMetrics();
  const segmentFilterController = useSegmentFilterController();
  const currentProject = isPublicMode() ? undefined : (_useCurrentProject = useCurrentProject()) === null || _useCurrentProject === void 0 ? void 0 : _useCurrentProject.get().value;
  const hasRequiredParams = function hasRequiredParams(): boolean {
    let currentBookmarkParams = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : bookmarkParams;
    let currentResults = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : results;
    return !!(currentBookmarkParams && currentResults);
  };
  const dataGroups = useMemo((): Record<string, DataGroup> => {
    if (props.dataGroups) {
      return props.dataGroups;
    }
    return dataGroupStore.value;
  }, [dataGroupStore.value, props.dataGroups]);
  const updatePieChartData = (currentBookmarkParams: InsightsBookmarkParams, currentResults: InsightsQueryResults): void => {
    if (hasRequiredParams(currentBookmarkParams, currentResults)) {
      var _meta$queryLimits;
      const updatedPieChartData = getPieChartData(currentBookmarkParams, metrics, currentResults, dataGroups, i18n);
      setPieChartData(updatedPieChartData);
      const {
        meta
      } = currentResults;
      const {
        apiResultsTruncated
      } = meta;
      segmentFilterController.loadSections([{
        items: updatedPieChartData.legendSegmentItems,
        readOnlyItems: updatedPieChartData.readOnlyLegendItems,
        tableColumns: updatedPieChartData.tableColumns,
        metadata: updatedPieChartData.legendIdToIndexMap,
        // Insights app is expected to use this mapping to persist legend selection indices to the URL
        apiResultsTruncated
      }], {
        tableSortConfig: getSortConfig({
          bookmarkSortConfig: currentBookmarkParams.sorting,
          headerCount: currentResults.headers.length
        })[ChartType.Pie],
        defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Pie].default,
        maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Pie].max
      });
      segmentFilterController.segmentationQueryLimit = (_meta$queryLimits = meta.queryLimits) === null || _meta$queryLimits === void 0 ? void 0 : _meta$queryLimits.limit;
      segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(currentProject) ? getInsightsAggregateCsvExportLimit(currentProject) : REPORT_LIMITS[ReportName.Insights].csvExport;
    } else {
      setPieChartData(null);
    }
  };
  const updatePersistedLegendSelections = (currentBookmarkParams: InsightsBookmarkParams): void => {
    var _currentBookmarkParam;
    const individualSelectedSegments = currentBookmarkParams === null || currentBookmarkParams === void 0 || (_currentBookmarkParam = currentBookmarkParams.legend) === null || _currentBookmarkParam === void 0 ? void 0 : _currentBookmarkParam.individualSelectedSegments;
    if (individualSelectedSegments) {
      segmentFilterController.loadFromSavedLegend(individualSelectedSegments ? [individualSelectedSegments] : null);
    }
  };
  useEffect(() => {
    if (!props.dataGroups && currentProject) {
      const projectId = currentProject.id;
      const workspaceId = parseInt(getWorkspaceId(), 10);
      // Note: data groups are only used in tooltips and don't need to trigger state updates/renders or be annotated with people properties
      dataGroupStore.fetch({
        projectId,
        workspaceId
      }); // async, no need to wait
    }
  }, [currentProject === null || currentProject === void 0 ? void 0 : currentProject.id, getWorkspaceId()]);
  useEffect(() => {
    const paramsAndResults = props.paramsAndResults;
    if (!isEqual(bookmarkParams, paramsAndResults.params) || !isEqual(results, paramsAndResults.results)) {
      setBookmarkParams(paramsAndResults.params);
      setResults(paramsAndResults.results);
      updatePieChartData(paramsAndResults.params, paramsAndResults.results);
      updatePersistedLegendSelections(paramsAndResults.params);
    }
  }, [props.paramsAndResults]);
  const formatValue = function formatValue(metricKey: string, value: string | number): string {
    let forTooltip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: results.meta.report_sections,
      bookmarkParams,
      dataGroups: Object.values(dataGroups)
    });
    const clause = metricClauseByKey[metricKey];
    return formatMetricNumberWithUnits(value, getFinalDisplayOptions((clause === null || clause === void 0 ? void 0 : clause.clauseType) === ClauseType.Show ? clause : null, eventPropertyDefinitionsStore, metrics, {},
    // Tooltip values should never be abbreviated.
    forTooltip ? {
      abbrev: false
    } : {}));
  };
  const handlePopoverClose = () => {
    const selectedPoint = popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    setPopoverSegment(null);
    setPopoverSelection(null);
    setPopoverStyles(null);
  };
  const handlePopoverAction = (ev: CustomEvent) => {
    var _props$onPopoverActio;
    const {
      path
    } = popoverSegment;
    const {
      color
    } = popoverSelection;
    (_props$onPopoverActio = props.onPopoverAction) === null || _props$onPopoverActio === void 0 || _props$onPopoverActio.call(props, {
      color: color as string,
      segment: path,
      action: ev.detail.action
    });
    handlePopoverClose();
  };
  const renderPopover = () => {
    if (!popoverSelection || !props.popoverCallback) {
      return null;
    }
    const segment = popoverSegment.path[popoverSegment.path.length - 1];
    const segments = popoverSegment.path;
    return <ContextMenuReact menuItems={props.popoverCallback(segment, segments)} relativePositioning open position={{
      left: 0,
      top: 0
    }} style={popoverStyles as any} onAction={(ev: CustomEvent) => handlePopoverAction(ev)} onClose={() => handlePopoverClose()} data-sentry-element="ContextMenuReact" data-sentry-component="renderPopover" data-sentry-source-file="index.tsx" />;
  };
  const handlePointClick = point => {
    var _props$popoverCallbac;
    const {
      series,
      options
    } = point;
    const segmentKey = options.name;
    const {
      segmentLookup
    } = pieChartData;
    const clickedPopoverSegment: FlatSegment = segmentLookup.get(segmentKey);
    const left = series.chart.tooltip.now.x;
    const top = series.chart.tooltip.now.y;
    const segment = clickedPopoverSegment.path[clickedPopoverSegment.path.length - 1];
    const segments = clickedPopoverSegment.path;
    const hasPopoverActions = (_props$popoverCallbac = props.popoverCallback) === null || _props$popoverCallbac === void 0 ? void 0 : _props$popoverCallbac.call(null, segment, segments).length;
    if (hasPopoverActions) {
      selectPoint(point);
      setPopoverSegment(clickedPopoverSegment);
      setPopoverSelection(point);
      setPopoverStyles({
        left: "".concat(left, "px"),
        top: "".concat(top, "px")
      });
    }
  };
  const loaded = props.loaded && hasRequiredParams() && !!pieChartData;
  if (!loaded) {
    return <div className='root'>
        <SpinnerReact elref='loadingSpinner' />
      </div>;
  }
  if (isEmpty(results === null || results === void 0 ? void 0 : results.series)) {
    return <div className='root'>
        <EmptyDataReact />
      </div>;
  }
  const visiblePieSeries: ChartDataItem[] = segmentFilterController.getSelectedItems(0).map(item => {
    const segment = pieChartData.segmentLookup.get(item.id);
    return {
      color: segmentFilterController.getItemColor(0, item.id),
      name: item.id,
      displayName: item.label,
      value: getSingleNumberFromSegmentValue(segment.value)
    };
  });

  // TODO pjboudrx maybe don't do this re-calc on every render?
  const otherSegment = canShowOtherSegment(bookmarkParams) ? getOtherSegment(pieChartData.segmentLookup, segmentFilterController, pieChartData.overallTotal) : null;
  if ((otherSegment === null || otherSegment === void 0 ? void 0 : otherSegment.totalValue) > 0) {
    visiblePieSeries.push({
      color: colors.otherSegment.primary,
      name: OTHER_SEGMENT_VALUE,
      displayName: "Other",
      secondaryColor: null,
      value: otherSegment.totalValue
    });
  }
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata: results.meta.report_sections,
    bookmarkParams,
    dataGroups: Object.values(dataGroups)
  });
  const tooltipFormatter = _ref => {
    let {
      name,
      value,
      percent,
      color
    } = _ref;
    if (name === OTHER_SEGMENT_VALUE) {
      return renderPieChartTooltip({
        color,
        formatter: v => formatValue(name, v, true),
        metricClauseByKey,
        i18n,
        otherMetricKeys: otherSegment.metricKeys,
        percent,
        value
      });
    }
    const segment = pieChartData.segmentLookup.get(name);
    const metricKey = segment.path[0];
    return renderPieChartTooltip({
      color,
      formatter: v => formatValue(metricKey, v, true),
      metricKey,
      metricClauseByKey,
      i18n,
      percent,
      segments: segment.path.slice(1).map((v, index) => renamePropertyValue(v, results.headers[index + 1], bookmarkParams.sections.group[index])),
      value
    });
  };
  const visibleSegmentIds = visiblePieSeries.map(segment => {
    return segment.name;
  });
  return <div className='root' data-sentry-component="InsightsPieChart" data-sentry-source-file="index.tsx">
      <SegmentLegendHeader segmentFilterController={segmentFilterController} visibleSegmentIds={visibleSegmentIds} data-sentry-element="SegmentLegendHeader" data-sentry-source-file="index.tsx" />
      <PieChartReact className='pie-chart' chartData={visiblePieSeries} tooltipFormatter={metricClauseByKey ? tooltipFormatter : null} onPieChartClick={props.popoverCallback && handlePointClick} getMetricKeyForName={name => {
      const segment = pieChartData.segmentLookup.get(name);
      return segment ? segment.path[0] : name;
    }} valueFormatter={(metricKey, value) => formatValue(metricKey, value)} data-sentry-element="PieChartReact" data-sentry-source-file="index.tsx" />
      {/* render point popover */}
      {renderPopover()}
    </div>;
};
export const InsightsPieChartReact = withStyles(InsightsPieChart, css);